import { FC } from 'react';

import { EditIcon, EyeIconSmall, TrashBinIcon } from '../../../../../../assets/icons/index';
import { Actions } from '../../../../../../components/actions/index';
import { AppFormattedMessage } from '../../../../../../components/app-formatted-message/index';
import { TableRow } from '../../../../../../components/table/index';
import { Tag, TagVariant } from '../../../../../../components/tag/index';
import { useFormat, useLocale } from '../../../../../../hooks/index';
import { selectedCompanyStorage } from '../../../../../../storage/company/selected-company.util';
import { AccessRole } from '../../../../../../types/stakeholder.types';
import { getTemplateSignatoryBaseDisplayLabel } from '../../../constants/template-signatory-type.constant';
import { PopupType } from '../../../enums/popup-type.const';
import { categoryLabelKey } from '../../../enums/template.const';
import { TemplateFilter } from '../../../enums/template-filter.const';
import { TemplateFormStore } from '../../../store/template-form.store';
import { TemplatePopupStore } from '../../../store/template-popup.store';
import { Column, columnOrderMap } from '../template-table.types';
import { MobileRow } from './template-mobile-row';
import { RowPayload, RowProps } from './template-row.types';

export const Row: FC<RowProps> = ({ data, mode }) => {
  const { format } = useFormat();
  const { messagesLocale } = useLocale();
  const company = selectedCompanyStorage(({ company }) => company);
  const isAuditor = company?.stakeholder?.accessRole === AccessRole.AUDITOR;
  const { name, category, status, signatoryTypes, updatedAt, id } = data;
  const isUsedTemplate = status === TemplateFilter.USED;

  const categoryLabel = category ? messagesLocale[categoryLabelKey[category]] : '-';

  const signatoriesLabel =
    Array.from(
      new Set(
        (signatoryTypes ?? []).map((s) =>
          getTemplateSignatoryBaseDisplayLabel(s.stakeholderType, messagesLocale),
        ),
      ),
    ).join(', ') || '-';

  const editedOnLabel = updatedAt ? format(new Date(updatedAt), 'dd MMM, yyyy') : '-';

  const openPopup = TemplatePopupStore(({ openPopup }) => openPopup);
  const toggleViewState = TemplateFormStore(({ toggleViewState }) => toggleViewState);
  const toggleEditState = TemplateFormStore(({ toggleEditState }) => toggleEditState);

  const handleViewDetails = () => toggleViewState({ id, state: true });
  const handleEditTemplate = () => toggleEditState({ id, state: true });

  const handleDelete = () =>
    openPopup({
      type: PopupType.DELETE_TEMPLATE,
      metadata: id,
      onAcceptCallback: () => {},
    });

  const rowActions = isAuditor
    ? [
        {
          title: messagesLocale['VIEW_DETAILS'],
          icon: <EyeIconSmall />,
          callback: handleViewDetails,
        },
      ]
    : [
        {
          title: messagesLocale['VIEW_DETAILS'],
          icon: <EyeIconSmall />,
          callback: handleViewDetails,
        },
        {
          title: messagesLocale['EDIT_TEMPLATE'],
          icon: <EditIcon />,
          callback: handleEditTemplate,
        },
        {
          title: messagesLocale['DELETE'],
          className: isUsedTemplate ? 'text-gray-300' : 'text-fireside-500',
          icon: <TrashBinIcon iconColor={isUsedTemplate ? '#D0D5DD' : '#F04438'} />,
          disabled: isUsedTemplate,
          tooltipContent: messagesLocale['TEMPLATE_DELETE_BLOCKED_TOOLTIP'],
          callback: handleDelete,
        },
      ];

  const payload: RowPayload = {
    [Column.NAME]: <span className="">{name}</span>,
    [Column.CATEGORY]: <span className="">{categoryLabel}</span>,
    [Column.STATUS]: (
      <Tag
        title={
          status === TemplateFilter.USED ? (
            <AppFormattedMessage id="FILTER_IN_USE" />
          ) : (
            <AppFormattedMessage id="FILTER_NOT_USED" />
          )
        }
        variant={status === TemplateFilter.USED ? TagVariant.GREEN : TagVariant.GRAY}
      />
    ),
    [Column.SIGNATORIES]: <span className="">{signatoriesLabel}</span>,
    [Column.EDITED_ON]: <span className="text-sm font-[450] text-gray-700">{editedOnLabel}</span>,
    [Column.ACTIONS]: <Actions actions={rowActions} />,
    id,
  };

  if (mode === 'table')
    return (
      <TableRow
        columnOrder={columnOrderMap}
        contentClassName={{ [Column.ACTIONS]: 'justify-end pr-6', general: 'pl-4 pr-2' }}
        fields={payload}
        labelClassName={{
          [Column.ACTIONS]: 'text-sm font-[450] text-gray-700',
          general: 'text-sm font-[450] text-gray-700',
        }}
        tdClassName={{ [Column.ACTIONS]: 'w-24 min-w-[96px]' }}
      />
    );

  return <MobileRow name={name} payload={payload} />;
};
